import type { ReactNode } from 'react';

import { Button } from '../Button';
import { Icon } from '../Icon';

/** What a human decided about a draft. */
export type DraftDecision = 'APPROVED' | 'REJECTED';

/** The record of that decision. */
export interface DraftReview {
  /** What was decided. */
  readonly decision: DraftDecision;
  /** Who decided, as it should read in the audit trail. */
  readonly reviewer: string;
  /** When, ISO-8601, if the moment is known. */
  readonly decidedAt?: string;
  /** The moment as the reader should see it, such as in the workspace's zone. */
  readonly decidedAtText?: string;
}

export interface DraftBannerProps {
  /** What the draft is, e.g. "Follow-up email to Dana Whitfield". */
  readonly subject: string;
  /** The decision, once one has been taken. Null means still awaiting review. */
  readonly review?: DraftReview | null;
  /** Called when the reviewer approves. */
  readonly onApprove?: () => void;
  /** Called when the reviewer rejects. */
  readonly onReject?: () => void;
  /** The draft content the banner labels. */
  readonly children?: ReactNode;
}

/** The exact wording the operating model requires. It is not paraphrased. */
export const DRAFT_LABEL = 'DRAFT — HUMAN REVIEW REQUIRED';

/**
 * The label that must sit on anything the assistant wrote.
 *
 * There is no dismiss control, and that omission is the component's entire
 * contract. A banner a reviewer can close is a banner that gets closed — and
 * the next person to open the record sees assistant text with nothing marking
 * it as such. The only ways out of the pending state are Approve and Reject,
 * both of which record who decided; the banner then keeps stating what happened
 * rather than disappearing, because "a human approved this" is itself something
 * the next reader needs to know.
 *
 * The pending banner is an `alert`: it is not incidental information, it is a
 * condition the reader must act on before the content beneath it means
 * anything. Once decided it steps down to a `status`.
 *
 * @param props see {@link DraftBannerProps}
 * @returns the banner, wrapping the draft it labels
 */
export function DraftBanner({
  subject,
  review = null,
  onApprove,
  onReject,
  children,
}: DraftBannerProps): React.JSX.Element {
  const pending = review === null;

  return (
    <div className="fo-draft" data-state={pending ? 'pending' : review.decision.toLowerCase()}>
      <div
        className="fo-draft__banner"
        role={pending ? 'alert' : 'status'}
        data-testid="draft-banner"
      >
        <Icon name={pending ? 'alert' : review.decision === 'APPROVED' ? 'check' : 'close'} />
        <div className="fo-draft__text">
          <p className="fo-draft__label">
            {pending
              ? DRAFT_LABEL
              : `${review.decision === 'APPROVED' ? 'APPROVED' : 'REJECTED'} DRAFT`}
          </p>
          <p className="fo-draft__subject">
            {subject}
            {pending ? null : (
              <>
                {' · '}
                <span className="fo-draft__reviewer">
                  {review.decision === 'APPROVED' ? 'Approved' : 'Rejected'} by {review.reviewer}
                  {review.decidedAt === undefined ? null : (
                    <>
                      {' on '}
                      <time dateTime={review.decidedAt}>
                        {review.decidedAtText ?? review.decidedAt}
                      </time>
                    </>
                  )}
                </span>
              </>
            )}
          </p>
        </div>

        {pending && (onApprove !== undefined || onReject !== undefined) ? (
          <div className="fo-draft__actions">
            <Button variant="primary" size="sm" icon="check" onClick={onApprove}>
              Approve
            </Button>
            <Button variant="danger" size="sm" icon="close" onClick={onReject}>
              Reject
            </Button>
          </div>
        ) : null}
      </div>

      {children === undefined ? null : <div className="fo-draft__content">{children}</div>}
    </div>
  );
}
